import moment from "moment";
import instance from "../../axios";
import {
  FALLBACK_VEHICLE_TYPES,
  PREVIEW_KEY,
  QUOTE_STATUS,
  buildQuoteRecord,
  calcLine,
  calcTotals,
  defaultTerms,
  mapVehicleToQuoteFields,
  normalizeQuote,
  normalizeStatusValue,
  parseVinDecoderResponse,
  resolveVatPercentage,
} from "./quoteUtils";

const unwrapList = (payload) => {
  const page = payload?.data || payload || {};
  const rows = Array.isArray(page.data)
    ? page.data
    : Array.isArray(page)
      ? page
      : [];
  return {
    data: rows,
    total: page.total || rows.length,
    page: page.page || 1,
    pageSize: page.pageSize || page.limit || rows.length || 20,
  };
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data?.data)) return value.data.data;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const toApiPayload = (quote) => {
  const now = quote.quoteDate || moment().toISOString();
  return {
    quote: {
      id:quote?.id??0,
      accountId: quote.accountId || null,
      accountName: quote.accountName || "",
      contractNumber: quote.contractNumber || "",
      customerName: quote.contactPerson || quote.customerName || "",
      contactPersonMobile: quote.mobile || quote.contactPersonMobile || "",
      email: quote.email || "",
      address: quote.address || "",
      vatNumber: quote.vatNumber || "",
      quoteRefNo: quote.quoteRefNo || quote.quoteNumber || "",
      quoteRefDate: quote.quoteRefDate || now,
      currentDate: quote.currentDate || moment().toISOString(),
      quoteDate: now,
      expiryDate: quote.expiryDate,
      status: normalizeStatusValue(quote.status) || QUOTE_STATUS.DRAFT,
      salePersonId: quote.salesPersonId || quote.salePersonId || null,
      vehicleId: quote.vehicleId || 0,
      created: quote.createdAt || quote.created || now,
      modified: quote.modified || null,
      attributes: {
        ...(quote.attributes || {}),
        salePersonName:
          quote.salesPersonName ||
          quote.salePersonName ||
          quote.attributes?.salePersonName ||
          "",
        salePersonEmail:
          quote.salesPersonEmail ||
          quote.salePersonEmail ||
          quote.attributes?.salePersonEmail ||
          "",
        salePersonPhone:
          quote.salesPersonMobile ||
          quote.salePersonPhone ||
          quote.salesPersonPhone ||
          quote.attributes?.salePersonPhone ||
          "",
        companyId: quote?.companyId || quote.attributes?.companyId || "0",
        licensePlate:
          quote.plate ||
          quote.licensePlate ||
          quote.attributes?.licensePlate ||
          "",
        vatPercentage: quote.vatPercentage ?? quote.attributes?.vatPercentage ?? "",
        mail: Boolean(quote.mail ?? quote.attributes?.mail),
        approvedBy:
          quote.approvedBy || quote.attributes?.approvedBy || "",
        rejectedBy:
          quote.rejectedBy || quote.attributes?.rejectedBy || "",
        approvedAt:
          quote.approvedAt || quote.attributes?.approvedAt || "",
        rejectedAt:
          quote.rejectedAt || quote.attributes?.rejectedAt || "",
      },
    },
    quoteItemCollection: (quote.items || []).map((item) => {
      const quantity = Number(item.quantity) || 0;
      const unitPrice = Number(item.unitPrice) || 0;
      const cost = Number(item.cost) || quantity * unitPrice;
      const saleVatAmount = Number(item.vatAmount || item.saleVatAmount) || 0;
      const saleNetAmount =
        Number(item.lineTotal || item.saleNetAmount) || cost + saleVatAmount;
      return {
        quoteId: quote.id || null,
        expenseTypeId: item.expenseTypeId || null,
        expenseSubTypeId: item.expenseSubTypeId || item.itemId || null,
        quantity,
        unitPrice,
        cost,
        saleVatAmount,
        saleNetAmount,
        expenseType: item.serviceType || item.expenseType || "",
        typeLabel: item.typeLabel || item.serviceType || item.expenseType || "",
        subTypeLabel: item.subTypeLabel || item.itemName || "",
        subTypeNum: item.subTypeNum || item.itemCode || "",
        description:
          item.description ||
          item.itemDescription ||
          item.remarks ||
          "",
        created: item.created || now,
        modified: item.modified || null,
      };
    }),
  };
};


const mapSalesPerson = (item) => {
  const entity = item?.entity || item || {};
  const id = entity.id ?? item?.id;
  if (id === null || id === undefined || id === "") return null;
  const name = entity.name || entity.label || item?.name || item?.label || entity.email || "";
  return {
    id,
    value: id,
    name,
    label: name,
    email: entity.email || item?.email || "",
    phone: entity.phone || entity.mobile || item?.phone || item?.mobile || "",
  };
};

const mergeSalesPersonOption = (rows, person) => {
  if (!person?.id) return rows;
  if (rows.some((row) => String(row.id) === String(person.id))) return rows;
  return [
    {
      id: person.id,
      value: person.id,
      name: person.name || person.label || person.email || "",
      label: person.label || person.name || person.email || "",
      email: person.email || "",
      phone: person.phone || person.mobile || "",
    },
    ...rows,
  ];
};

export async function searchSalesPeople(search, logInUser, limit = 20) {
  try {
    const data = await instance.get(
      `/api/users/getAllUsersInSystem?search=${encodeURIComponent(
        search || "",
      )}&userTypes=4&limit=${limit}`,
    );
    const rows = asArray(data)
      .map(mapSalesPerson)
      .filter(Boolean);
    const seen = new Set();
    const unique = [];
    rows.forEach((row) => {
      const key = String(row.id);
      if (seen.has(key)) return;
      seen.add(key);
      unique.push(row);
    });
    return mergeSalesPersonOption(unique);
  } catch (error) {
    return  [];
  }
}

const mapExpenseSubType = (item, expenseTypeId) => ({
  ...item,
  id: item.id,
  parentId: item.parentId || expenseTypeId,
  expenseTypeId: item.parentId || item.expenseTypeId || expenseTypeId,
  label: item.label || item.name,
  name: item.label || item.name,
  unitPrice: item.unitPrice,
  purchasePrice: item.purchasePrice,
  subTypeNum: item.subTypeNum,
  categoryName: item.categoryName,
});

export async function listQuotes({
  page = 1,
  pageSize = 20,
  status = "",
  accountName = "",
  customerName = "",
  quoteRefNo = "",
  quoteNo = "",
}) {
  let url = `/api/quotes/get?page=${page}&limit=${pageSize}`;
  if (status) {
    url += `&status=${encodeURIComponent(status)}`;
  }
  if (accountName) {
    url += `&accountname=${encodeURIComponent(accountName)}`;
  }
  if (customerName) {
    url += `&customername=${encodeURIComponent(customerName)}`;
  }
  if (quoteRefNo) {
    url += `&quoterefno=${encodeURIComponent(quoteRefNo)}`;
  }
  if (quoteNo) {
    url += `&quoteno=${encodeURIComponent(quoteNo)}`;
  }
  const payload = await instance.get(url);
  const pageData = unwrapList(payload);
  return {
    ...pageData,
    data: (pageData.data || []).map(normalizeQuote),
  };
}

export async function getQuote(id) {
  if (String(id) === "preview") {
    const preview = sessionStorage.getItem(PREVIEW_KEY);
    return preview ? normalizeQuote(JSON.parse(preview)) : null;
  }
  const quote = await instance.get(`/api/quotes/${id}`);
  if (!quote) return null;

  let items =
    quote.quoteItemCollection ||
    quote.items ||
    quote.quoteItems ||
    [];
  if (!Array.isArray(items) || !items.length) {
    try {
      const itemsRes = await instance.get(`/api/quotes/items/${id}`);
      items = asArray(itemsRes);
    } catch (error) {
      items = [];
    }
  }

  const storedVatPercentage =
    quote.vatPercentage ?? quote.attributes?.vatPercentage ?? null;
  const hasStoredVatPercentage =
    storedVatPercentage !== null &&
    storedVatPercentage !== undefined &&
    storedVatPercentage !== "";

  let normalized = normalizeQuote({
    ...quote,
    quoteItemCollection: items,
  });

  if (!hasStoredVatPercentage) {
    const linkedAccountId = quote.accountId || quote.account?.id;
    if (linkedAccountId) {
      try {
        const details = await getAccountById(linkedAccountId);
        if (details) {
          const vatPercentage = resolveVatPercentage(details);
          const vatItems = (normalized.items || []).map((row) =>
            calcLine(row, vatPercentage / 100),
          );
          normalized = {
            ...normalized,
            vatPercentage,
            items: vatItems,
            ...calcTotals(vatItems),
          };
        }
      } catch (error) {
        // keep default resolution when account lookup fails
      }
    }
  }

  if (normalized.vehicleId) {
    try {
      const vehicle = await instance.get(`/api/vehicles/${normalized.vehicleId}`);
      if (vehicle) {
        const mapped = mapVehicleToQuoteFields(vehicle);
        return {
          ...normalized,
          ...mapped,
          vehicleId: normalized.vehicleId,
          plate:
            normalized.plate ||
            mapped.plate ||
            quote.attributes?.licensePlate ||
            "",
          salesPersonName:
            normalized.salesPersonName ||
            quote.attributes?.salePersonName ||
            "",
        };
      }
    } catch (error) {
      // keep quote without vehicle enrichment
    }
  }

  return normalized;
}

export async function saveQuote(form, extras = {}) {
  const isUpdate = Boolean(extras.id);
  const quote = buildQuoteRecord(form, {
    ...extras,
    quoteDate: extras.quoteDate || moment().toISOString(),
    status: extras.status || QUOTE_STATUS.DRAFT,
  });
  const url = isUpdate ? `/api/quotes/${extras.id}` : "/api/quotes";
  const method = isUpdate ? "put" : "post";
  const saved = await instance[method](url, toApiPayload(quote));
  const payload = saved || quote;
  return normalizeQuote({
    ...quote,
    ...(payload?.quote || payload),
    items:
      payload?.quoteItemCollection ||
      payload?.quoteItems ||
      payload?.items ||
      quote.items,
  });
}

export async function updateQuoteStatus(quoteId, status, user) {
  if (!quoteId) {
    throw new Error("Quote id is required");
  }
  const nextStatus = normalizeStatusValue(status);
  if (
    nextStatus !== QUOTE_STATUS.APPROVED &&
    nextStatus !== QUOTE_STATUS.REJECTED
  ) {
    throw new Error("Invalid quote status");
  }

  // Raw API data only — no normalizeQuote / toApiPayload (keeps attributes as-is)
  const apiQuote = await instance.get(`/api/quotes/${quoteId}`);
  if (!apiQuote) {
    throw new Error("Quote not found");
  }

  const rawQuote =
    apiQuote.quote && typeof apiQuote.quote === "object"
      ? apiQuote.quote
      : apiQuote;

  let quoteItemCollection =
    apiQuote.quoteItemCollection ||
    rawQuote.quoteItemCollection ||
    apiQuote.items ||
    rawQuote.items ||
    apiQuote.quoteItems ||
    rawQuote.quoteItems ||
    [];

  if (!Array.isArray(quoteItemCollection) || !quoteItemCollection.length) {
    try {
      quoteItemCollection = asArray(
        await instance.get(`/api/quotes/items/${quoteId}`),
      );
    } catch (error) {
      quoteItemCollection = [];
    }
  }

  const actorName = user?.name || user?.email || "";
  const now = moment().toISOString();
  const attributes = { ...(rawQuote.attributes || {}) };

  if (nextStatus === QUOTE_STATUS.APPROVED) {
    attributes.approvedBy = actorName;
    attributes.approvedAt = now;
    attributes.rejectedBy = "";
    attributes.rejectedAt = "";
  } else {
    attributes.rejectedBy = actorName;
    attributes.rejectedAt = now;
    attributes.approvedBy = "";
    attributes.approvedAt = "";
  }

  const saved = await instance.put(`/api/quotes/${quoteId}`, {
    quote: {
      ...rawQuote,
      status: nextStatus,
      attributes,
    },
    quoteItemCollection,
  });

  return normalizeQuote(
    saved?.quote ||
      saved || {
        ...rawQuote,
        status: nextStatus,
        attributes,
      },
  );
}

export async function deleteQuote(id) {
  await instance.delete(`/api/quotes/${id}`);
  return true;
}

/** Mark quote attributes.mannualInvoice = true so it cannot be converted again. */
export async function markQuoteManualInvoiceCreated(quoteId) {
  if (!quoteId) {
    throw new Error("Quote id is required");
  }

  const apiQuote = await instance.get(`/api/quotes/${quoteId}`);
  if (!apiQuote) {
    throw new Error("Quote not found");
  }

  const rawQuote =
    apiQuote.quote && typeof apiQuote.quote === "object"
      ? apiQuote.quote
      : apiQuote;

  let quoteItemCollection =
    apiQuote.quoteItemCollection ||
    rawQuote.quoteItemCollection ||
    apiQuote.items ||
    rawQuote.items ||
    apiQuote.quoteItems ||
    rawQuote.quoteItems ||
    [];

  if (!Array.isArray(quoteItemCollection) || !quoteItemCollection.length) {
    try {
      quoteItemCollection = asArray(
        await instance.get(`/api/quotes/items/${quoteId}`),
      );
    } catch (error) {
      quoteItemCollection = [];
    }
  }

  const attributes = {
    ...(rawQuote.attributes || {}),
    mannualInvoice: true,
  };

  const saved = await instance.put(`/api/quotes/${quoteId}`, {
    quote: {
      ...rawQuote,
      attributes,
    },
    quoteItemCollection,
  });

  return normalizeQuote(
    saved?.quote ||
      saved || {
        ...rawQuote,
        attributes,
      },
  );
}

export async function searchAccounts(search, userId) {
  try {
    const response = await instance.get(
      `/api/accounts/getlimiteddata?search=${encodeURIComponent(
        search || "",
      )}&all=true&userId=${userId}`,
    );
    return asArray(response).map((acc) => ({
      ...acc,
      label: acc?.name || acc?.label,
      value: acc.id,
    }));
  } catch (error) {
    return [];
  }
}

async function getAccountById(accountId) {
  const full = await instance.get(`/api/accounts?accountId=${accountId}`);
  if (Array.isArray(full)) return full[0] || null;
  if (Array.isArray(full?.data)) return full.data[0] || null;
  return full || null;
}

async function getAccountTerms(accountId) {
  const termsRes = await instance.get(`/api/accounts/${accountId}/terms`);
  const termAttrs =
    termsRes?.data?.attributes ||
    termsRes?.attributes ||
    termsRes?.data ||
    {};
  const defaults = defaultTerms();
  return {
    termsEnglish:
      termAttrs.termsEnglishMaintenance ||
      termAttrs.termsEnglish ||
      defaults.termsEnglish,
    termsArabic:
      termAttrs.termsArabicMaintenance ||
      termAttrs.termsArabic ||
      defaults.termsArabic,
  };
}

export async function loadAccountForQuote(account) {
  if (!account) return { details: null, terms: defaultTerms() };
  let details = account;
  try {
    details = (await getAccountById(account.id)) || account;
  } catch (error) {
    details = account;
  }
  let terms = defaultTerms();
  return { details, terms };
}

export async function getVehicleTypeNames() {
  try {
    const payload = await instance.get(
      `/api/vehiclecategorys/get?page=1&limit=500&category=`,
    );
    const names = asArray(payload)
      .map((row) =>
        typeof row === "string"
          ? row
          : row.categoryName || row.name || row.label,
      )
      .filter(Boolean);
    return names.length ? names : FALLBACK_VEHICLE_TYPES;
  } catch (error) {
    return FALLBACK_VEHICLE_TYPES;
  }
}

export async function getMakesPage(page = 1, search = "") {
  const data = await instance({
    method: "GET",
    url: `/api/makes/get?page=${page}&limit=20&make=${encodeURIComponent(
      search,
    )}`,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
  const list = Array.isArray(data?.data) ? data.data : [];
  const total = parseInt(data?.total, 10) || 0;
  return {
    options: list.map((item) => ({
      id: item.id,
      key: item.makeName,
      label: item.makeName,
      value: item.makeName,
    })),
    hasNext: Boolean(data?.hasNext ?? total > page * 20),
  };
}

export async function getVehicleModels(selectedMake) {
  if (!selectedMake) return [];
  try {
    const response = await instance({
      method: "GET",
      url: `/api/models/get?limit=1000&make=${encodeURIComponent(
        selectedMake,
      )}&search= `,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
    const source = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];
    return source.map((item) => ({
      ...item,
      label: item.modelName,
    }));
  } catch (error) {
    return [];
  }
}

export async function getExpenseTypesByContract(contractNumber="bfmc000", cancelToken) {
  if (!contractNumber) return [];
  const data = await instance.get(
    `/api/expensetypes/${encodeURIComponent(
      contractNumber,
    )}`,
    cancelToken ? { signal: cancelToken } : undefined,
  );
  const rows = data?.data || data || [];
  return Array.isArray(rows) ? rows : [];
}

const expenseSubTypeCache = new Map();

export async function getExpenseSubTypes(expenseTypeId, search = "") {
  if (!expenseTypeId) return [];
  const cacheKey = `${expenseTypeId}::${String(search || "").trim()}`;
  const cached = expenseSubTypeCache.get(cacheKey);
  if (cached) return cached;

  const request = (async () => {
    try {
      const data = await instance.get(
        `/api/expensetypes/subtype/bfmc000?expenseTypeId=${expenseTypeId}&page=1&limit=20&search=${encodeURIComponent(
          search || "",
        )}`,
      );
      const source = asArray(data);
      return (Array.isArray(source) ? source : []).map((item) =>
        mapExpenseSubType(item, expenseTypeId),
      );
    } catch (error) {
      expenseSubTypeCache.delete(cacheKey);
      return [];
    }
  })();

  expenseSubTypeCache.set(cacheKey, request);
  return request;
}

export async function decodeVin(vin) {
  const result = await instance.get(`/api/trigger/vindecoder2?vin=${vin}`);
  return parseVinDecoderResponse(result) || result;
}

export const QUOTE_VEHICLE_PARENT_ID = 64;

const resolveModelId = (modelId) => {
  if (!modelId) return "";
  if (typeof modelId === "object") return modelId.id || "";
  const numeric = Number(modelId);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : "";
};

export const buildVehiclePayload = (form, extras = {}) => {
  const makeLabel = form.make?.label || form.make?.key || form.make || "";
  const modelLabel =
    form.modelId?.modelName || form.modelId?.label || form.model || "";
  const year = form.year || "";
  const fuelType = form.fuelType?.id || form.fuelType || "";
  const label =
    form.label ||
    [makeLabel, modelLabel, year].filter(Boolean).join(" ") ||
    form.plate ||
    form.vin ||
    "";
  const cityId =
    form.cityId?.id ||
    (typeof form.cityId === "object" ? form.cityId?.value : form.cityId) ||
    "";
  const cityLabel =
    form.cityId?.cityEn || form.cityId?.name || form.cityId?.label || "";
  return {
    id: 0,
    attributes: {
      typeName: ["Maintenance"],
      country: "United Arab Emirates Dirham",
      make: makeLabel,
      year,
      fuel_type: fuelType,
      cityId: cityId || "",
      city: cityLabel,
      cityEn: cityLabel,
      createdBy: extras.createdBy || 0,
    },
    deviceId: "",
    label,
    modelId: resolveModelId(form.modelId),
    fuelEfficiency: 0,
    odometer: 0,
    garage: "",
    entitlement: 0,
    vin: form.vin || "",
    vehicleLicensePlate: form.plate || "",
    leasePeriod: "",
    vehicleLisencePlateArabic: "",
    totalAvailablekm: 200,
    expirationTime: null,
    complianceTime: null,
    insuranceTime: null,
    monthlyRate: "",
    purchasePrice: "",
    purchaseDate: null,
    deliveryDate: null,
    leaseEndDate: null,
    leaseStartDate: null,
    areaId: "",
    cityId: cityId || 1,
    category: form.vehicleType || "",
    onLease: false,
    parentId: form.parentId || QUOTE_VEHICLE_PARENT_ID,
    carbonFootPrint: 0,
  };
};

export async function searchVehicleByVin(vin,user) {
  if (!vin) return null;
  const payload = await instance.get(
    `/api/vehicles/get?userId=${user?.id||QUOTE_VEHICLE_PARENT_ID}&all=true&page=1&limit=50&search=${encodeURIComponent(
      vin,
    )}`,
  );
  const rows = asArray(payload);
  if (!rows.length) return null;
  const exact = rows.find(
    (row) =>
      String(row.vin || "")
        .replace(/\s/g, "")
        .toUpperCase() === String(vin).replace(/\s/g, "").toUpperCase(),
  );
  return exact || rows[0] || null;
}

export async function createQuoteVehicle(form, extras = {}) {
  const payload = buildVehiclePayload(form, extras);
  return instance.post("/api/vehicles", payload);
}
